import { useState, useEffect, useRef } from 'preact/hooks';

/**
 * Shared fade-on-interaction hook used by all floating action buttons in the chat view.
 *
 * Returns `true` when the user tapped the chat area or scrolled it (signals FABs to fade out).
 * Auto-clears after 600ms (scroll) or 2000ms (tap).
 *
 * @param selfRef   Ref to the FAB's root element — taps ON the FAB itself do NOT trigger fade.
 * @param getInhibit  Optional function; when it returns true, fade is suppressed (e.g. when panel is expanded).
 *                    Accessed via a ref internally so callers do NOT need to pass deps.
 */
export function useChatAreaFade(
  selfRef: { current: Element | null },
  getInhibit?: () => boolean,
): boolean {
  const [scrollFaded, setScrollFaded] = useState(false);
  // Keep inhibit always fresh without re-registering listeners on every render.
  const inhibitRef = useRef(getInhibit);
  inhibitRef.current = getInhibit;

  useEffect(() => {
    const chatArea = document.querySelector('.chat-area');
    if (!chatArea) return;
    let fadeTimeout: ReturnType<typeof setTimeout> | null = null;
    const fade = (ms: number) => {
      if (inhibitRef.current?.()) return;
      setScrollFaded(true);
      if (fadeTimeout) clearTimeout(fadeTimeout);
      fadeTimeout = setTimeout(() => setScrollFaded(false), ms);
    };
    const onScroll = () => fade(600);
    const onTap = (e: Event) => {
      if (selfRef.current?.contains(e.target as Node)) return;
      fade(2000);
    };
    chatArea.addEventListener('scroll', onScroll, { passive: true });
    chatArea.addEventListener('pointerdown', onTap, { passive: true });
    return () => {
      chatArea.removeEventListener('scroll', onScroll);
      chatArea.removeEventListener('pointerdown', onTap);
      if (fadeTimeout) clearTimeout(fadeTimeout);
    };
  }, []); // single registration — inhibit freshness handled by inhibitRef

  return scrollFaded;
}
